const durationFallback = (key, params = {}) => {
  const n = params.n;
  switch (key) {
    case 'REPORT.DURATION.SECONDS':
      return `${n} sec.`;
    case 'REPORT.DURATION.MINUTES':
      return `${n} min.`;
    case 'REPORT.DURATION.MINUTES_SECONDS':
      return `${params.minutes} min. ${params.seconds} sec.`;
    case 'REPORT.DURATION.HOURS':
      return `${n} h.`;
    case 'REPORT.DURATION.HOURS_MINUTES':
      return `${params.hours} h. ${params.minutes} min.`;
    case 'REPORT.DURATION.DAYS':
      return `${n} d.`;
    case 'REPORT.DURATION.DAYS_HOURS':
      return `${params.days} d. ${params.hours} h.`;
    case 'REPORT.EXACT_SECONDS':
      return `Exact value: ${params.seconds} sec.`;
    default:
      return '';
  }
};

const translateDuration = (t, key, params) =>
  typeof t === 'function' ? t(key, params) : durationFallback(key, params);

export const formatSecondsLabel = (seconds, t) =>
  translateDuration(t, 'REPORT.DURATION.SECONDS', {
    n: Math.round(Number(seconds)),
  });

export const durationMetricTooltip = (t, definition, seconds) => {
  const parts = [];
  if (definition) parts.push(definition);
  if (
    seconds !== null &&
    seconds !== undefined &&
    seconds !== '' &&
    !Number.isNaN(Number(seconds))
  ) {
    parts.push(
      translateDuration(t, 'REPORT.EXACT_SECONDS', {
        seconds: Math.round(Number(seconds)),
      })
    );
  }
  return parts.join(' ');
};

export const formatReportDuration = (timeInSeconds, t) => {
  if (
    timeInSeconds === null ||
    timeInSeconds === undefined ||
    timeInSeconds === ''
  ) {
    return '--';
  }

  const seconds = Math.round(Number(timeInSeconds));
  if (Number.isNaN(seconds)) return timeInSeconds;

  if (seconds < 60) {
    return formatSecondsLabel(seconds, t);
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (!remainingSeconds) {
      return translateDuration(t, 'REPORT.DURATION.MINUTES', { n: minutes });
    }
    return translateDuration(t, 'REPORT.DURATION.MINUTES_SECONDS', {
      minutes,
      seconds: remainingSeconds,
    });
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes
      ? translateDuration(t, 'REPORT.DURATION.HOURS_MINUTES', {
          hours,
          minutes: remainingMinutes,
        })
      : translateDuration(t, 'REPORT.DURATION.HOURS', { n: hours });
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours
    ? translateDuration(t, 'REPORT.DURATION.DAYS_HOURS', {
        days,
        hours: remainingHours,
      })
    : translateDuration(t, 'REPORT.DURATION.DAYS', { n: days });
};

export const formatTime = timeInSeconds => {
  if (!timeInSeconds) {
    return '';
  }

  if (timeInSeconds < 60) {
    return formatSecondsLabel(timeInSeconds);
  }

  if (timeInSeconds < 3600) {
    const minutes = Math.floor(timeInSeconds / 60);
    return `${minutes}m`;
  }

  if (timeInSeconds < 86400) {
    const hours = Math.floor(timeInSeconds / 3600);
    return `${hours}h`;
  }

  const days = Math.floor(timeInSeconds / 86400);
  return `${days}d`;
};

export const GROUP_BY_FILTER = {
  1: { id: 1, period: 'day' },
  2: { id: 2, period: 'week' },
  3: { id: 3, period: 'month' },
  4: { id: 4, period: 'year' },
  5: { id: 5, period: 'hour' },
};

export const GROUP_BY_OPTIONS = {
  HOUR: {
    id: 'HOUR',
    period: 'hour',
    translationKey: 'REPORT.GROUPING_OPTIONS.HOUR',
  },
  DAY: {
    id: 'DAY',
    period: 'day',
    translationKey: 'REPORT.GROUPING_OPTIONS.DAY',
  },
  WEEK: {
    id: 'WEEK',
    period: 'week',
    translationKey: 'REPORT.GROUPING_OPTIONS.WEEK',
  },
  MONTH: {
    id: 'MONTH',
    period: 'month',
    translationKey: 'REPORT.GROUPING_OPTIONS.MONTH',
  },
  YEAR: {
    id: 'YEAR',
    period: 'year',
    translationKey: 'REPORT.GROUPING_OPTIONS.YEAR',
  },
};

export const DATE_RANGE_OPTIONS = {
  LAST_7_DAYS: {
    id: 'LAST_7_DAYS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS',
    offset: 6,
    groupByOptions: [GROUP_BY_OPTIONS.DAY],
  },
  LAST_30_DAYS: {
    id: 'LAST_30_DAYS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS',
    offset: 29,
    groupByOptions: [GROUP_BY_OPTIONS.DAY, GROUP_BY_OPTIONS.WEEK],
  },
  LAST_3_MONTHS: {
    id: 'LAST_3_MONTHS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS',
    offset: 89,
    groupByOptions: [
      GROUP_BY_OPTIONS.DAY,
      GROUP_BY_OPTIONS.WEEK,
      GROUP_BY_OPTIONS.MONTH,
    ],
  },
  LAST_6_MONTHS: {
    id: 'LAST_6_MONTHS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS',
    offset: 179,
    groupByOptions: [GROUP_BY_OPTIONS.WEEK, GROUP_BY_OPTIONS.MONTH],
  },
  LAST_YEAR: {
    id: 'LAST_YEAR',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_YEAR',
    offset: 364,
    groupByOptions: [GROUP_BY_OPTIONS.WEEK, GROUP_BY_OPTIONS.MONTH],
  },
  CUSTOM_DATE_RANGE: {
    id: 'CUSTOM_DATE_RANGE',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE',
    offset: null,
    groupByOptions: [
      GROUP_BY_OPTIONS.DAY,
      GROUP_BY_OPTIONS.WEEK,
      GROUP_BY_OPTIONS.MONTH,
      GROUP_BY_OPTIONS.YEAR,
    ],
  },
};

export const CHART_FONT_FAMILY =
  'Inter,-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

export const DEFAULT_LINE_CHART = {
  type: 'line',
  fill: false,
  borderColor: '#10A37F',
  pointBackgroundColor: '#10A37F',
};

export const DEFAULT_BAR_CHART = {
  type: 'bar',
  backgroundColor: '#10A37F',
};

const createChartConfig = yAxisTickCallback => ({
  datasets: [DEFAULT_BAR_CHART],
  scales: {
    x: {
      ticks: {
        fontFamily: CHART_FONT_FAMILY,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
    y: {
      type: 'linear',
      position: 'left',
      ticks: {
        fontFamily: CHART_FONT_FAMILY,
        beginAtZero: true,
        stepSize: 1,
        callback: yAxisTickCallback,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
});

export const DEFAULT_CHART = createChartConfig((value, index, ticks) => {
  if (!index || index === ticks.length - 1) {
    return value;
  }
  return '';
});

export const TIME_CHART_CONFIG = createChartConfig((value, index, values) => {
  if (!index || index === values.length - 1) {
    return formatTime(value);
  }
  return '';
});

export const METRIC_CHART = {
  conversations_count: DEFAULT_CHART,
  incoming_messages_count: DEFAULT_CHART,
  outgoing_messages_count: DEFAULT_CHART,
  avg_first_response_time: TIME_CHART_CONFIG,
  reply_time: TIME_CHART_CONFIG,
  avg_resolution_time: TIME_CHART_CONFIG,
  avg_chat_duration_with_bot: TIME_CHART_CONFIG,
  avg_chat_duration_operators_only: TIME_CHART_CONFIG,
  resolutions_count: DEFAULT_CHART,
  bot_resolutions_count: DEFAULT_CHART,
  bot_handoffs_count: DEFAULT_CHART,
};

export const OVERVIEW_METRICS = {
  open: 'OPEN',
  unattended: 'UNATTENDED',
  unassigned: 'UNASSIGNED',
  pending: 'PENDING',
  online: 'ONLINE',
  busy: 'BUSY',
  offline: 'OFFLINE',
};
